/// <amd-module name="Artifacts/Cargo/redux/Sagas" />

import { CargoBatchOperationType } from "Artifacts/Cargo/Contracts/CargoApi";
import { CargoActionTypes } from "Artifacts/Cargo/redux/Actions";
import { IFeedCargoDataService } from "Artifacts/Cargo/services/CargoDataService";
import { ICargoRestApiUrlGeneratorService } from "Artifacts/Cargo/services/CargoRestApiUrlGeneratorService";
import { PackageActions } from "Artifacts/Package/redux/Actions";
import {
  BatchGenerator,
  IBatchIterator,
  TransformingBatchGenerator,
} from "Artifacts/Package/services/BatchHandling";
import { PerfScenarios } from "Artifacts/Root/Constants";
import { getFeedNameWithNewView } from "Artifacts/Root/helpers/FeedHelpers";
import { FeedPerformanceHelper } from "Artifacts/Root/helpers/FeedPerformanceHelper";
import {
  IDefaultActionPayload,
  IDeleteActionPayload,
  IListActionPayload,
  IPromoteActionPayload,
  IRestoreActionPayload,
  IUpstreamOverrideActionPayload,
} from "Artifacts/Root/Protocols/types/ICommandActionPayloads";
import { RootActions } from "Artifacts/Root/redux/Actions";
import * as Res from "Artifacts/Root/Resources";
import { all, call, getContext, put, takeEvery } from "Artifacts/Root/Saga";
import { IPackageVersionIdentity } from "Artifacts/Root/types";
import {
  BatchListData,
  MinimalPackageDetails,
  UpstreamingBehavior,
  UpstreamVersionVisibility,
} from "Artifacts/Root/types/PackagingShared";
import { SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssScenario } from "VSS/Platform/Performance";

export function* rootSaga(): SagaIterator {
  yield takeEvery(CargoActionTypes.Promote, promote);
  yield takeEvery(CargoActionTypes.Download, download);
  yield takeEvery(CargoActionTypes.SoftDelete, softDelete);
  yield takeEvery(CargoActionTypes.Restore, restore);
  yield takeEvery(CargoActionTypes.PermanentDelete, permanentDelete);
  yield takeEvery(CargoActionTypes.SetUpstreamOverride, setUpstreamOverride);
  yield takeEvery(CargoActionTypes.YankOrUnyank, changeListStatus);
}

export function* promote(
  action: ActionWithPayload<CargoActionTypes.Promote, IPromoteActionPayload>,
): Generator {
  try {
    yield put(RootActions.promoteCommandStarted());
    const { feed, versions, viewId, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedCargoDataService>("IFeedCargoDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchPromote], projectId, feed.id, viewId, iterator);

    // retrieve the view so the name can be applied on the selected versions.
    const view = feed.views.find((v) => v.id === viewId);

    yield put(RootActions.promoteCommandCompleted());
    yield call(onSuccessCallback, versions, view?.name);
  } catch (error) {
    yield put(RootActions.promoteCommandFailed(error.message));
  }
}

export function* download(
  action: ActionWithPayload<CargoActionTypes.Download, IDefaultActionPayload>,
): Generator {
  const { feed, versions } = action.payload;

  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  const service = pageContext.getService<ICargoRestApiUrlGeneratorService>(
    "ICargoRestApiUrlGeneratorService",
  );

  const results = (yield all(
    versions.map((v) => {
      return call(
        [service, service.getPackageDownloadUrl],
        getFeedNameWithNewView(feed.id, feed.selectedViewId),
        v.name,
        v.normalizedVersion,
        feed && feed.project != null,
        feed.accountMetadata.publicAccessMapping,
      );
    }),
  ) as unknown) as string[];

  results.forEach((downloadPackageUrl) => {
    // The first time the user clicks the donwload button, they will get redirected to SPS for authentication.
    // If we hit the download url in the existing window, when the client gets redirected, the window will be stuck on SPS.
    // So we need to open a new window to download the package.
    const newWindow = window.open(downloadPackageUrl as string, "_blank");
    if (newWindow != null) {
      newWindow.opener = null;
    }
  });
}

export function* softDelete(
  action: ActionWithPayload<CargoActionTypes.SoftDelete, IDeleteActionPayload>,
): Generator {
  try {
    yield put(RootActions.softDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedCargoDataService>("IFeedCargoDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (version: IPackageVersionIdentity) => {
        return {
          id: version.name,
          version: version.version,
        };
      },
    );
    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchDelete], projectId, feed.id, iterator);

    yield put(RootActions.softDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    yield put(RootActions.softDeleteCommandFailed(error.message));
  }
}

export function* restore(
  action: ActionWithPayload<CargoActionTypes.Restore, IRestoreActionPayload>,
): Generator {
  yield put(RootActions.restoreCommandStarted());
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionRestore
        : PerfScenarios.MultiplePackageVersionsRestoredToFeed,
    );

    const dataService = pageContext.getService<IFeedCargoDataService>("IFeedCargoDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (packageVersion: IPackageVersionIdentity) => {
        return {
          id: packageVersion.name,
          version: packageVersion.version,
        };
      },
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchRestoreToFeed], projectId, feed.id, iterator);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.restoreCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.restoreCommandFailed(error.message));
  }
}

export function* permanentDelete(
  action: ActionWithPayload<CargoActionTypes.PermanentDelete, IDeleteActionPayload>,
): Generator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    yield put(RootActions.permDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionPermanentDelete
        : PerfScenarios.MultiplePackageVersionsPermanentDeleted,
    );

    const dataService = pageContext.getService<IFeedCargoDataService>("IFeedCargoDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchPermanentlyDelete], projectId, feed.id, iterator);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.permDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.permDeleteCommandFailed(error.message));
  }
}

export function* setUpstreamOverride(
  action: ActionWithPayload<CargoActionTypes.SetUpstreamOverride, IUpstreamOverrideActionPayload>,
): Generator {
  try {
    yield put(PackageActions.upstreamOverrideCommandStarted());
    const { feed, packageName, allowExternalVersions, onSuccessCallback } = action.payload;

    const behavior = {
      versionsFromExternalUpstreams: allowExternalVersions
        ? UpstreamVersionVisibility.AllowExternalVersions
        : UpstreamVersionVisibility.Auto,
    } as UpstreamingBehavior;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;

    const dataService = pageContext.getService<IFeedCargoDataService>("IFeedCargoDataService");

    const projectId = feed.project ? feed.project.id : "";

    yield call(
      [dataService, dataService.setUpstreamingBehavior],
      feed.id,
      packageName,
      behavior,
      projectId,
    );

    yield put(PackageActions.upstreamOverrideCommandCompleted(allowExternalVersions));
    yield call(onSuccessCallback);
  } catch (error) {
    yield put(PackageActions.upstreamOverrideCommandFailed(error.message));
  }
}

export function* changeListStatus(
  action: ActionWithPayload<CargoActionTypes.YankOrUnyank, IListActionPayload>,
): Generator {
  try {
    yield put(RootActions.unlistRelistCommandStarted());
    const { feed, versions, isListed, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const cargoDataService = pageContext.getService<IFeedCargoDataService>("IFeedCargoDataService");

    const batchRequest = {
      data: { listed: isListed } as BatchListData,
      operation: CargoBatchOperationType.Yank,
      packages: versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
    };

    const projectId = feed.project ? feed.project.id : "";

    yield call(
      [cargoDataService, cargoDataService.updatePackageVersions],
      batchRequest,
      projectId,
      feed.id,
    );

    yield put(RootActions.unlistRelistCommandCompleted());
    yield call(onSuccessCallback, versions, isListed);
  } catch (error) {
    const errorMessage = error.status === 401 ? Res.Error_ErrorUserUnauthorized : error.message;
    yield put(RootActions.unlistRelistCommandFailed(errorMessage));
  }
}
